/* ==========================================================================
   StethoMe EU — zachowania sekcji strony głównej (17.09.2026)

   Ładowany tylko przez index.html, po main.js. Podstrony go nie widzą.
   Cztery niezależne bloki, każdy wychodzi cicho, jeśli nie znajdzie
   swojego markupu — dzięki temu wycięcie sekcji ze strony nie wywala
   reszty skryptu.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Karuzela łukowa (arc-pro-carousel)
   -------------------------------------------------------------------------- */

/* Przepisane 17.09.2026 — dwa zgłoszenia Martiny: „laguje się" i „jest
   ucięta", plus żywa referencja arcprocarousel.framer.website.

   GEOMETRIA. Kąt między kartami nie jest stałą. W referencji karty leżą
   na łuku krawędź w krawędź, więc odstęp kątowy wynika z długości łuku:
   step = (szerokość karty + odstęp) / promień. Liczę go z realnie
   zmierzonej karty i promienia z CSS, dzięki czemu łuk wygląda tak samo
   na 390 i na 1920 px, zamiast robić się gęsty albo rzadki.

   WYDAJNOŚĆ. Poprzednia wersja pisała co klatkę pięć właściwości do
   każdej z dziewięciu kart (transform, opacity, zIndex, scale,
   pointerEvents) — z czego trzy wymuszają przeliczenie układu albo
   ponowne rasteryzowanie. Teraz:
   - co klatkę zmienia się WYŁĄCZNIE `--a` (czyli transform), i tylko
     wtedy, gdy kąt faktycznie się zmienił o więcej niż 0,05°,
   - opacity/z-index/pointer-events są pisane tylko przy zmianie stanu
     widoczności, nie w każdej klatce,
   - zniknęła maska na kontenerze: maska to osobna warstwa, która
     przy każdej klatce kompozytowała całe poddrzewo od nowa. Ucinaniem
     zajmuje się teraz overflow sekcji, tak jak w referencji.
   - pętla staje, kiedy nic się nie rusza (brak dryfu, brak
     przeciągania, karuzela poza ekranem). */
(function () {
  const arc = document.querySelector("[data-arc]");
  if (!arc) return;

  const items = [...arc.querySelectorAll(".arc__item")];
  const n = items.length;
  if (!n) return;

  const VISIBLE_TO = 62;   // stopni — dalej karta i tak jest poza ekranem
  const DRIFT = 0.00009;   // kart na milisekundę, ok. 11 s na jedną kartę

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let step = 20;           // stopni między kartami, przeliczane w measure()
  let pxPerCard = 240;
  let offset = 0;
  let velocity = 0;
  let dragging = false;
  let lastX = 0;
  let lastT = 0;
  let onScreen = true;
  let hovering = false;
  let running = false;

  // Ostatnio zapisane wartości — bez tego każda klatka nadpisywałaby
  // te same stringi i tak samo kosztowała.
  const prevAngle = new Array(n).fill(NaN);
  const prevShown = new Array(n).fill(null);

  const measure = function () {
    const box = items[0].getBoundingClientRect();
    const cardW = box.width || 240;

    // Promień czytany z transform-origin karty, NIE z --arc-radius.
    // Własna właściwość CSS jest „obliczona jako zapisana": getPropertyValue
    // oddaje dosłowny string "clamp(430px, 57vw, 920px)", którego parseFloat
    // czyta jako NaN i cicho wchodzi na wartość zapasową — na telefonie
    // dawało to krok dwa razy za mały. transform-origin jest już rozwinięty
    // do pikseli, a jego składowa Y to dokładnie wysokość karty + promień.
    const originY = parseFloat(getComputedStyle(items[0]).transformOrigin.split(" ")[1]);
    const radius = Number.isFinite(originY) && originY > box.height
      ? originY - box.height
      : 800;

    // Odstęp między kartami: 6% szerokości karty, jak w referencji.
    step = ((cardW * 1.06) / radius) * (180 / Math.PI);
    pxPerCard = Math.max(120, cardW * 0.9);
  };

  const render = function () {
    for (let i = 0; i < n; i++) {
      let d = (i - offset) % n;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;

      const angle = d * step;
      const el = items[i];

      // Number.isFinite, a nie samo porównanie: prevAngle startuje jako NaN,
      // a każde porównanie z NaN daje false — przy pierwszym renderze warunek
      // nigdy by nie zadziałał i karty zostałyby bez kąta.
      if (!Number.isFinite(prevAngle[i]) || Math.abs(angle - prevAngle[i]) > 0.05) {
        el.style.setProperty("--a", angle.toFixed(2) + "deg");
        prevAngle[i] = angle;
      }

      // Widoczność przełącza się rzadko, więc pisana jest tylko przy
      // zmianie — nie co klatkę.
      const shown = Math.abs(angle) <= VISIBLE_TO;
      if (shown !== prevShown[i]) {
        el.style.opacity = shown ? "1" : "0";
        el.style.pointerEvents = shown ? "" : "none";
        el.style.zIndex = shown ? String(60 - Math.round(Math.abs(angle))) : "0";
        prevShown[i] = shown;
      }
    }
  };

  const idle = function () {
    return !dragging && velocity === 0 && (reduced || hovering || !onScreen);
  };

  const tick = function (t) {
    const dt = Math.min(48, lastT ? t - lastT : 16);
    lastT = t;

    if (!dragging) {
      if (Math.abs(velocity) > 0.00002) {
        offset += velocity * dt;
        velocity *= Math.pow(0.9955, dt); // tarcie
      } else {
        velocity = 0;
        if (!reduced && !hovering && onScreen) offset += DRIFT * dt;
      }
    }

    offset = ((offset % n) + n) % n;
    render();

    if (idle()) { running = false; lastT = 0; return; }
    requestAnimationFrame(tick);
  };

  const start = function () {
    if (running) return;
    running = true;
    lastT = 0;
    requestAnimationFrame(tick);
  };

  arc.addEventListener("pointerdown", (e) => {
    dragging = true;
    velocity = 0;
    lastX = e.clientX;
    arc.setPointerCapture(e.pointerId);
    start();
  });

  arc.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    lastX = e.clientX;
    const d = -dx / pxPerCard;
    offset = (((offset + d) % n) + n) % n;
    velocity = d / 16;
    render();
  });

  const endDrag = function (e) {
    if (!dragging) return;
    dragging = false;
    if (e && e.pointerId !== undefined && arc.hasPointerCapture(e.pointerId)) {
      arc.releasePointerCapture(e.pointerId);
    }
    start();
  };

  arc.addEventListener("pointerup", endDrag);
  arc.addEventListener("pointercancel", endDrag);

  // Kółko przewija karuzelę tylko przy geście poziomym (gładzik,
  // shift+scroll). Pionowe zostawiamy stronie — przechwycone
  // zablokowałoby przewijanie na sekcji wysokiej na pół ekranu.
  arc.addEventListener(
    "wheel",
    (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      offset = (((offset + e.deltaX / pxPerCard) % n) + n) % n;
      velocity = 0;
      render();
    },
    { passive: false }
  );

  arc.addEventListener("pointerenter", () => { hovering = true; });
  arc.addEventListener("pointerleave", () => { hovering = false; start(); });

  new IntersectionObserver(
    ([entry]) => { onScreen = entry.isIntersecting; if (onScreen) start(); },
    { threshold: 0 }
  ).observe(arc);

  // Krok kątowy zależy od zmierzonej szerokości karty, więc przy zmianie
  // okna trzeba go policzyć od nowa — inaczej po przejściu progu
  // clamp() karty zaczęłyby na siebie zachodzić albo się rozjeżdżać.
  const ro = new ResizeObserver(() => {
    measure();
    prevAngle.fill(NaN);
    render();
  });
  ro.observe(arc);

  measure();
  render();
  start();
})();

/* --------------------------------------------------------------------------
   Poziome akordeony — karty odbiorców i trzy kroki
   -------------------------------------------------------------------------- */

/* Jedna funkcja na dwie sekcje, bo od 17.09 obie chodzą tak samo:
   - .aud  — „Dla Rodzin / Dla Profesjonalistów", dwie karty,
   - .steps3 — trzy kroki, wzorowane na
     framer.com/m/CollapsingCards-gB5ZlD.js (referencja Martiny).

   Karty to <button aria-expanded>, nie <details>, bo to wybór jednego
   z kilku widoków, a nie rozwijanie szczegółów. Otwarta jest zawsze
   dokładnie jedna — kliknięcie otwartej nic nie robi, bo zamknięcie
   wszystkich zostawiłoby sekcję pustą.

   Szerokości są we frakcjach, więc suma zawsze wypełnia rząd niezależnie
   od szerokości okna, a animacja idzie po jednej właściwości siatki
   zamiast po trzech osobnych szerokościach kart. */
const collapsingRow = function (root, opts) {
  if (!root) return;

  const cards = [...root.querySelectorAll(opts.card)];
  if (cards.length < 2) return;

  const grid = opts.grid ? root.querySelector(opts.grid) : root;
  if (!grid) return;

  const cols = function (activeIndex) {
    return cards
      .map((_, i) => (i === activeIndex ? opts.open : opts.closed))
      .join(" ");
  };

  const open = function (index) {
    cards.forEach((card, i) => {
      const active = i === index;
      card.setAttribute("aria-expanded", String(active));
      // Linki w zamkniętej karcie wypadają z tab ordera — inaczej Tab
      // zatrzymywałby się na czymś, czego nie widać.
      card.querySelectorAll("a").forEach((link) => {
        link.tabIndex = active ? 0 : -1;
      });
    });
    grid.style.setProperty(opts.var, cols(index));
  };

  // opts.toggle (Martina, 23.09.2026): na karcie "Dla Rodzin / Dla
  // Profesjonalistów" kliknięcie już otwartej karty ma ją zamykać, zamiast
  // nic nie robić — w przeciwieństwie do trzech kroków (.steps3), gdzie
  // dokładnie jedna karta musi zostać otwarta zawsze.
  const closeAll = function () {
    cards.forEach((card) => {
      card.setAttribute("aria-expanded", "false");
      card.querySelectorAll("a").forEach((link) => {
        link.tabIndex = -1;
      });
    });
    grid.style.setProperty(opts.var, cards.map(() => "1fr").join(" "));
  };

  cards.forEach((card, i) => {
    card.addEventListener("click", (e) => {
      // Klik w link wewnątrz karty ma prowadzić na stronę, nie przełączać.
      if (e.target.closest("a")) return;
      if (opts.toggle && card.getAttribute("aria-expanded") === "true") {
        closeAll();
        return;
      }
      open(i);
    });
    card.addEventListener("pointerenter", () => {
      if (window.matchMedia("(min-width: 801px)").matches) open(i);
    });
    // Strzałki lewo/prawo po kartach, jak w zakładkach WAI-ARIA.
    card.addEventListener("keydown", (e) => {
      const last = cards.length - 1;
      let next = null;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") next = i === last ? 0 : i + 1;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = i === 0 ? last : i - 1;
      if (next === null) return;
      e.preventDefault();
      open(next);
      cards[next].focus();
    });
  });

  const initial = cards.findIndex((c) => c.getAttribute("aria-expanded") === "true");
  open(initial < 0 ? 0 : initial);
};

collapsingRow(document.querySelector("[data-audience]"), {
  card: ".aud__card",
  grid: ".aud__grid",
  var: "--aud-cols",
  open: "1.35fr",
  closed: "1fr",
  toggle: true,
});

// Proporcje z modułu Framera: aktywna 540/1136 ≈ 47,5%, zwinięte po 24%,
// czyli mniej więcej 2fr do 1fr do 1fr.
collapsingRow(document.querySelector("[data-collapsing]"), {
  card: ".steps3__card",
  grid: null,
  var: "--cc-cols",
  open: "2fr",
  closed: "1fr",
});

/* --------------------------------------------------------------------------
   Wideo — fasada zamiast osadzonego YouTube'a
   -------------------------------------------------------------------------- */

/* Dopóki nikt nie kliknie, na stronie leży miniatura i przycisk. Iframe
   powstaje dopiero na klik, z autoplay, więc użytkownik klika raz, a nie
   dwa razy. Przed kliknięciem strona nie pobiera nic z domen Google'a. */
(function () {
  document.querySelectorAll("[data-video]").forEach((box) => {
    const btn = box.querySelector(".video__play");
    if (!btn) return;

    btn.addEventListener("click", () => {
      const id = box.dataset.video;
      const iframe = document.createElement("iframe");
      iframe.src =
        `https://www.youtube-nocookie.com/embed/${id}` +
        "?autoplay=1&rel=0&modestbranding=1&hl=pl";
      iframe.title = box.dataset.videoTitle || "Wideo";
      iframe.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
      iframe.allowFullscreen = true;
      box.replaceChildren(iframe);
    });
  });
})();

/* --------------------------------------------------------------------------
   Wejście sekcji przy przewijaniu
   -------------------------------------------------------------------------- */

(function () {
  const targets = document.querySelectorAll("[data-reveal]");
  if (!targets.length) return;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    targets.forEach((el) => el.classList.add("is-in"));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        io.unobserve(entry.target); // jednorazowo — to nie jest efekt scrollowany
      });
    },
    { rootMargin: "0px 0px -12% 0px", threshold: 0.05 }
  );

  targets.forEach((el) => io.observe(el));
})();

/* --------------------------------------------------------------------------
   Opinie — karuzela „depth"
   -------------------------------------------------------------------------- */

/* Port komponentu z ../testimonials-depth-embed.html, czyli tego, który
   stoi na żywej stronie stethome.com/pl/kup-stethome-family. Logika
   przeniesiona bez zmian — te same stałe, ta sama obsługa zawijania
   przez szew.

   20.09.2026, per Martina: awatary-zdjęcia zamienione na kółko z literą
   (styl Google review), a cytaty rozwinięte do PEŁNEGO tekstu opinii —
   wcześniej karty pokazywały tylko przycięte fragmenty. Pięć pierwszych
   to te same opinie z Trustpilot/Google Play co wcześniej, tylko
   nieprzycięte (pełne teksty i źródła: ../testimonials-fan-embed.html,
   komentarze przy TESTIMONIALS tam). Cztery kolejne (Karolina Jabłońska,
   Radosław, Agnieszka, Bernadeta) to prawdziwe opinie z sekcji „Opinie
   o StethoMe®" na żywej stethome.com — pobrane wprost z DOM-u tej
   strony 20.09.2026, żeby się nie powtarzały z pierwszą piątką. Prof.
   Anna Preis z tej samej sekcji pominięta celowo — to opinia eksperta
   (akustyk), nie klienta, więc nie pasuje do „opinii użytkowników". */
(function () {
  const stage = document.getElementById("tdStage");
  const prevBtn = document.getElementById("tdPrev");
  const nextBtn = document.getElementById("tdNext");
  const backdrop = document.getElementById("tdBackdrop");
  const section = document.querySelector(".td");
  if (!stage || !prevBtn || !nextBtn) return;

  // Cztery odcienie marki (te same co w testimonials-fan-embed.html —
  // analogiczne i dopełniające wobec brand blue), przydzielane po kolei,
  // nie losowo, żeby ta sama opinia zawsze miała ten sam kolor.
  const AVATAR_COLORS = ["#009fe3", "#4338ca", "#0d8a6f", "#c2530e", "#001c2b"];

  const CHECK_ICON = '<svg viewBox="0 0 24 24"><path d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5z"/></svg>';
  const QUOTE_ICON =
    '<svg viewBox="0 0 18 14" preserveAspectRatio="none" aria-hidden="true">' +
    '<path d="M4.5 0C2 0 0 2.2 0 5c0 3.6 2.2 6.7 6 8.4l.9-1.7C4.4 10.4 3 8.5 2.8 6.4 3.2 6.6 3.6 6.7 4 6.7 5.8 6.7 7.2 5.2 7.2 3.4S5.8 0 4.5 0z"/>' +
    '<path d="M14.5 0C12 0 10 2.2 10 5c0 3.6 2.2 6.7 6 8.4l.9-1.7c-2.5-1.3-3.9-3.2-4.1-5.3.4.2.8.3 1.2.3 1.8 0 3.2-1.5 3.2-3.3S15.8 0 14.5 0z"/>' +
    "</svg>";
  const CLOSE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  const TESTIMONIALS = [
    {
      quote: "Działanie stetoskopu potwierdzone przez dwóch różnych lekarzy. Znacznie ograniczyło konieczność wizyt lekarskich. Obsługa jest prosta, intuicyjna, natomiast trzeba połączyć wynik z obserwacją dziecka. Nasłuchiwanie oddechu plus wynik z urządzenia pomaga podjąć decyzję, czy czas na wizytę, czy można poradzić sobie samemu przy odpowiednim zapasie lekarstw i zgodzie lekarza na samodzielne działanie w danej sytuacji (szczególnie w weekendy i święta).",
      name: "Monika Dykalska",
      source: "Trustpilot",
      verified: false,
    },
    {
      quote: "Odkąd moje dzieci zaczęły chodzić do przedszkola, infekcje stały się nieodłącznym i bardzo częstym elementem naszego życia. StethoMe znacznie ułatwił nam życie w tamtym czasie. Jest bardzo intuicyjny i prosty w obsłudze. Kiedy tylko miałam wątpliwości, czy moje dziecko jest osłuchowo „czyste”, StethoMe w parę minut dawał mi odpowiedź, która w 100% pokrywała się z tym, co stwierdzał lekarz. Korzystam z niego od prawie 2 lat i prędko nie przestanę, bo jest bardzo przydatnym urządzeniem nie tylko przy małych dzieciach, ale i dla dorosłych. W naszym domu każdy ma w aplikacji swój profil i jak tylko mamy podejrzenia, że coś jest nie tak, to sprawdzamy. Zmniejszyliśmy w ten sposób liczbę wizyt u lekarza. Naprawdę polecam każdemu rodzicowi.",
      name: "Natalia",
      source: "Trustpilot",
      verified: false,
    },
    {
      quote: "Używam stetoskopu już bardzo długo. Bardzo dokładnie bada. Potwierdza się później z diagnozą lekarza.",
      name: "Karolina",
      source: "Trustpilot",
      verified: true,
    },
    {
      quote: "Wybawienie dla rodziców dzieci z astmą wczesnodziecięcą. Wszystko śmiga, łatwa obsługa, szybka pomoc mailowa i rozwiązanie problemu.",
      name: "Marlena",
      source: "Trustpilot",
      verified: true,
    },
    {
      quote: "Świetne urządzenie. Intuicyjna obsługa i bardzo łatwe w użytkowaniu. Instrukcja postępowania wyświetla się na telefonie. W razie pogorszenia astmy stetoskop pozwala uspokoić obawy albo podjąć działania. Konfiguracja i sparowanie z telefonem zajęło kilka minut. Polecam wszystkim rodzicom dzieci z astmą.",
      name: "Jadwiga",
      source: "Google Play",
      verified: false,
    },
    {
      quote: "To jest coś, czego szukałam od czasu urodzenia pierwszego dziecka, wtedy miałam tylko termometr. Brak wiarygodnego urządzenia, które mogłoby monitorować stan zdrowia moich dzieci, był dla mnie bardzo uciążliwy. Nawet teraz denerwuję się, kiedy wspominam, ile czasu spędziłam, czekając na wizytę u lekarza, kiedy jedno z moich dzieci kaszlało i nie mogło złapać oddechu, a ja wiedziałam, co się dzieje. Nawet teraz, gdy moje dzieci są już starsze, kupiłabym to urządzenie i używała za każdym razem, gdy są chore. To wspaniałe, że w końcu ktoś je wymyślił!",
      name: "Karolina Jabłońska",
      source: "Mama dwójki dzieci",
      verified: false,
    },
    {
      quote: "Używam StethoMe już 2 lata. Przez ten czas mieliśmy podejrzenie zapalenia płuc u mojej żony. Stetoskop pokazał, że płuca są czyste, co dało lekarzowi podstawy do innego leczenia. Nasz synek ma też astmę, więc przy infekcjach sprawdzamy, czy jego kaszel to zaostrzenie czy kolejna przedszkolna infekcja. StethoMe przydał się też na wakacjach w Tajlandii — nie musieliśmy go używać, ale sama jego obecność dała poczucie ulgi, że nie będziemy musieli jechać do nieznanego lekarza przy każdym kaszlu. Nie wiem, jak można żyć bez StethoMe.",
      name: "Radosław",
      source: "Klient StethoMe®",
      verified: false,
    },
    {
      quote: "Posiadam stetoskop od miesiąca i naprawdę żałuję, że tak późno trafił w moje ręce. Aplikacja jest mega prosta w obsłudze, a zarazem spełnia wiele funkcji, np. analizę badania, a nawet możliwość wysłania nagrania do lekarza. Moim zdaniem każdy rodzic astmatyka powinien mieć to urządzenie w swoim domu — daje możliwość reagowania w czas, czy to weekend, czy święto, czy wyjazd na urlop, kiedy nie ma możliwości dostać się do lekarza. Panowie z obsługi klienta są bardzo mili i pomocni, na każde pytanie dostawałam ekspresową odpowiedź. StethoMe jest warte każdych pieniędzy, a spokój rodzica o zdrowie dziecka jest bezcenny. Polecam!",
      name: "Agnieszka",
      source: "Facebook",
      verified: false,
    },
    {
      quote: "Używam stetoskopu od 3 miesięcy i jest to cud techniki i marzenie dla wszystkich astmatyków. Szybko, bezbłędnie, w domowym zaciszu pozwala wykonać wiarygodne badanie płuc, jak i kontrolować w pełni leczenie astmy. Co więcej, pełna współpraca lekarska i spokój rodzica dziecka chorego na astmę jest nieoceniona. Jestem zachwycona stetoskopem, ułatwił nam proces leczenia i diagnozy lekarskiej w 100%. Dziękuję w imieniu swoim i mojego syna.",
      name: "Bernadeta",
      source: "Google Play",
      verified: false,
    },
  ];

  const total = TESTIMONIALS.length;
  let current = Math.floor(total / 2); // start w środku, rozmyte karty po obu stronach od pierwszej klatki
  let expandedCard = null;

  function closeExpanded() {
    if (!expandedCard) return;
    expandedCard.classList.remove("td-card--expanded");
    expandedCard.style.opacity = "";
    expandedCard.style.filter = "";
    expandedCard.style.zIndex = "";
    expandedCard.style.pointerEvents = "";
    expandedCard = null;
    if (section) section.classList.remove("td--lightbox-open");
    render();
  }

  function openExpanded(card) {
    closeExpanded();
    card.classList.add("td-card--expanded");
    // 27.09.2026: force-visible regardless of the card's prior carousel
    // position — a card the render() loop had marked opacity:0/low
    // z-index (offscreen distance > 2) stayed invisible even once
    // ".td-card--expanded" repositioned it front-and-center, so the
    // backdrop was the only thing anyone actually saw ("cały ekran ma
    // nałożony jakiś transparentny kolor" — Martina). z-index 1000 is
    // comfortably above .td-backdrop's 999.
    card.style.opacity = "1";
    card.style.filter = "none";
    card.style.zIndex = "1000";
    card.style.pointerEvents = "auto";
    expandedCard = card;
    if (section) section.classList.add("td--lightbox-open");
  }

  if (backdrop) backdrop.addEventListener("click", closeExpanded);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeExpanded();
  });

  const cardEls = TESTIMONIALS.map((t, i) => {
    const initial = t.name.trim().charAt(0).toUpperCase();
    const card = document.createElement("article");
    card.className = "td-card";
    card.innerHTML =
      '<button type="button" class="td-card__close" aria-label="Zamknij opinię">' + CLOSE_ICON + "</button>" +
      '<div class="td-card__header">' +
        '<div class="td-card__avatar" style="background:' + AVATAR_COLORS[i % AVATAR_COLORS.length] + '">' +
          initial +
          (t.verified ? '<span class="td-card__verified">' + CHECK_ICON + "</span>" : "") +
        "</div>" +
        "<div>" +
          '<div class="td-card__name">' + t.name + "</div>" +
          '<div class="td-card__source">' + t.source + "</div>" +
        "</div>" +
      "</div>" +
      '<hr class="td-card__divider" />' +
      '<p class="td-card__quote">' + t.quote + "</p>" +
      '<button type="button" class="td-card__more">Czytaj więcej</button>' +
      '<div class="td-card__footer"><div class="td-card__mark">' + QUOTE_ICON + "</div></div>";
    stage.appendChild(card);

    const quoteEl = card.querySelector(".td-card__quote");
    const moreBtn = card.querySelector(".td-card__more");
    moreBtn.addEventListener("click", () => openExpanded(card));
    card.querySelector(".td-card__close").addEventListener("click", closeExpanded);

    // "Czytaj więcej" tylko wtedy, gdy przycięcie faktycznie coś ucina —
    // krótsze opinie (np. Karolina z Trustpilot) mieszczą się w całości.
    requestAnimationFrame(() => {
      if (quoteEl.scrollHeight <= quoteEl.clientHeight + 1) moreBtn.style.display = "none";
    });

    return card;
  });

  // Przesunięcie/skala/rozmycie/przechył na każdy krok od karty w centrum.
  const STEP_X = 130;
  const STEP_SCALE = 0.08;
  const STEP_BLUR = 1.2;
  const STEP_TILT = 5;

  // Karuzela kołowa: signedOffset to najkrótsza droga od `current` do
  // karty i, w którąkolwiek stronę — to dzięki temu next/prev zawijają
  // się w nieskończoność zamiast zatrzymywać na końcach.
  function signedOffset(i) {
    let offset = i - current;
    if (offset > total / 2) offset -= total;
    if (offset < -total / 2) offset += total;
    return offset;
  }

  function styleFor(offset) {
    const abs = Math.abs(offset);
    const visible = abs <= 2;
    const scale = (1 - Math.min(abs, 2) * STEP_SCALE) * (visible ? 1 : 0.9);
    const opacity = !visible ? 0 : abs === 0 ? 1 : abs === 1 ? 0.75 : 0.45;
    const tilt = offset * STEP_TILT;
    return {
      transform:
        "translate(-50%, -50%) translateX(" + offset * STEP_X + "px) scale(" + scale + ") rotate(" + tilt + "deg)",
      opacity: opacity,
      zIndex: 10 - abs,
      pointerEvents: abs === 0 ? "auto" : "none",
    };
  }

  // Przy każdym przejściu jedna karta „przeskakuje szew" (np. z lewego
  // skraju wprost na prawy). Zwykła animacja przesunęłaby ją przez cały
  // stos. Naprawa: tej jednej karcie wyłączamy przejście, przestawiamy
  // ją bezgłośnie o krok dalej po tej stronie, na którą ma wjechać,
  // wymuszamy reflow, przywracamy przejście i dopiero ustawiamy cel.
  let lastOffsets = cardEls.map((_, i) => signedOffset(i));

  function render() {
    const offsets = cardEls.map((_, i) => signedOffset(i));

    // Faza 1: przeskok kart zawijających, z wyłączonymi przejściami —
    // dla całej partii NARAZ i z jednym wspólnym reflow. (Wplecenie
    // tego per karta w tę samą pętlę, która ustawia cele pozostałych,
    // było pierwotnym błędem: reflow każdej karty „utrwalał" to, co
    // ustawiono wcześniejszym, zabijając im przejścia.)
    let needsReflow = false;
    cardEls.forEach((card, i) => {
      if (Math.abs(offsets[i] - lastOffsets[i]) > 1) {
        const farSide = offsets[i] > 0 ? offsets[i] - total : offsets[i] + total;
        const s = styleFor(farSide);
        card.style.transition = "none";
        card.style.transform = s.transform;
        card.style.opacity = s.opacity;
        needsReflow = true;
      }
    });
    if (needsReflow) void stage.offsetWidth;
    cardEls.forEach((card, i) => {
      if (Math.abs(offsets[i] - lastOffsets[i]) > 1) card.style.transition = "";
    });

    // Faza 2: prawdziwy cel każdej karty, w osobnym przebiegu.
    cardEls.forEach((card, i) => {
      if (card === expandedCard) return; // 27.09.2026: don't let the carousel's own render() clobber an open lightbox card's forced style
      const s = styleFor(offsets[i]);
      card.style.transform = s.transform;
      card.style.opacity = s.opacity;
      card.style.filter = "blur(" + Math.min(Math.abs(offsets[i]), 2) * STEP_BLUR + "px)";
      card.style.zIndex = s.zIndex;
      card.style.pointerEvents = s.pointerEvents;
    });

    lastOffsets = offsets;
  }

  prevBtn.addEventListener("click", () => { current = (current - 1 + total) % total; render(); });
  nextBtn.addEventListener("click", () => { current = (current + 1) % total; render(); });

  render();
})();

/* --------------------------------------------------------------------------
   Nagrania badań (sekcja „Jak działa StethoMe®") — 18.09.2026
   -------------------------------------------------------------------------- */

/* Ten sam mechanizm co na Webflow: iframe'y media-api potrzebują tokenu
   JWT ważnego godzinę, więc src składamy dopiero po jego pobraniu.
   Slider to natywny scroll-snap — strzałki i kropki tylko przewijają. */
(function () {
  const root = document.querySelector("[data-recordings]");
  if (!root) return;

  const frames = [...root.querySelectorAll("iframe.sounds-frame")];
  fetch("https://europe-central2-webpage-examples-router.cloudfunctions.net/fetchJWTToken")
    .then((r) => r.text())
    .then((token) => {
      frames.forEach((f) => {
        f.src = "https://prod.media-api.stethome.com/v2/compound/frame/" +
          f.dataset.id + "/" + f.dataset.point + "/pl?details=0&bearer=" + token;
      });
    })
    .catch(() => {});

  const track = root.querySelector(".recordings__track");
  const slides = [...track.children];
  const prev = root.querySelector("[data-recordings-prev]");
  const next = root.querySelector("[data-recordings-next]");
  const dotsWrap = root.querySelector("[data-recordings-dots]");

  const dots = slides.map((_, i) => {
    const b = document.createElement("button");
    b.type = "button";
    b.setAttribute("aria-label", "Nagranie " + (i + 1));
    b.addEventListener("click", () => go(i));
    dotsWrap.appendChild(b);
    return b;
  });

  function index() {
    const x = track.scrollLeft;
    let best = 0;
    slides.forEach((s, i) => {
      if (Math.abs(s.offsetLeft - track.offsetLeft - x) <
          Math.abs(slides[best].offsetLeft - track.offsetLeft - x)) best = i;
    });
    return best;
  }

  function go(i) {
    track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft });
  }

  function update() {
    const i = index();
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    dots.forEach((d, j) => d.setAttribute("aria-current", j === i ? "true" : "false"));
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = atEnd;
  }

  prev.addEventListener("click", () => go(Math.max(0, index() - 1)));
  next.addEventListener("click", () => go(Math.min(slides.length - 1, index() + 1)));
  track.addEventListener("scroll", () => requestAnimationFrame(update), { passive: true });
  window.addEventListener("resize", update);
  update();
})();
